import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, SlidersHorizontal, MapPin, Heart, Star, Gift, Coffee, Utensils, Pizza, Beef, MoreHorizontal, ChevronRight, Sparkles, Users, TrendingUp, Plus, Target } from "lucide-react";
import api from "../lib/api";
import { distanceKm } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { LiveMap } from "../components/LiveMap";

const CATS = [
  { key: "Café", icon: Coffee },
  { key: "Asiatique", icon: Utensils },
  { key: "Italien", icon: Pizza },
  { key: "Burger", icon: Beef },
  { key: "Plus", icon: MoreHorizontal },
];

function useGeo() {
  const { t } = useI18n();
  const [pos, setPos] = useState(null);
  const locate = useCallback((notify = false) => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => { if (notify) toast.error(t("geoDenied")); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [t]);
  useEffect(() => { locate(false); }, [locate]);
  return { pos, locate };
}

function BusinessCard({ b }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/businesses/${b.id}`)} className="w-44 shrink-0 overflow-hidden rounded-2xl border border-cardline bg-card2/60 text-left transition-colors hover:border-gold/60" data-testid={`business-card-${b.id}`}>
      <div className="relative h-28">
        <img src={b.image} alt={b.name} className="h-full w-full object-cover" />
        {b.new && <span className="absolute left-2 top-2 rounded-full bg-gold px-2 py-0.5 text-[10px] font-bold text-ink">{t("new")}</span>}
        <span className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full glass text-white"><Heart size={14} /></span>
      </div>
      <div className="p-3">
        <div className="truncate font-heading text-sm font-semibold">{b.name}</div>
        <div className="text-xs text-slate-400">{b.tag}</div>
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-slate-400"><MapPin size={12} /> {b.distance}</span>
          <span className="flex items-center gap-1 rounded-full bg-gold/15 px-1.5 py-0.5 font-semibold text-gold"><Star size={11} fill="currentColor" /> {b.rating}</span>
        </div>
      </div>
    </button>
  );
}

function CreatorHome() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pos, locate } = useGeo();
  const [businesses, setBusinesses] = useState([]);
  const [missions, setMissions] = useState([]);
  const notified = useRef(false);
  const [fCat, setFCat] = useState("all");
  const [fDist, setFDist] = useState(0);
  const [fBudget, setFBudget] = useState(0);

  useEffect(() => {
    api.get("/businesses").then(({ data }) => setBusinesses(data.businesses)).catch(() => {});
    api.get("/campaigns").then(({ data }) => setMissions(data.campaigns)).catch(() => {});
  }, []);

  const nearby = pos
    ? missions.filter((m) => m.lat && m.lng && distanceKm(pos, { lat: m.lat, lng: m.lng }) <= 8)
    : [];

  const cats = Array.from(new Set(businesses.map((b) => b.category))).filter(Boolean);
  const within = (o) => !pos || fDist === 0 || (o.lat && distanceKm(pos, { lat: o.lat, lng: o.lng }) <= fDist);
  const fBusinesses = businesses.filter((b) => (fCat === "all" || b.category === fCat) && within(b));
  const fMissions = missions.filter((m) => (fCat === "all" || m.category === fCat) && (m.budget >= fBudget) && within(m));

  useEffect(() => {
    if (pos && nearby.length > 0 && !notified.current) {
      notified.current = true;
      toast.success(`🎯 ${nearby.length} ${t("missionsNear")} !`);
    }
  }, [pos, nearby.length, t]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">{t("hello")} {user?.name?.split(" ")[0]} 👋</h1>
        <p className="mt-1 text-sm text-slate-400">{t("readyDiscover")}</p>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-2xl border border-cardline bg-card2/60 px-4 py-3">
          <Search size={18} className="text-slate-400" />
          <input data-testid="home-search-input" placeholder={t("searchBusiness")} className="w-full bg-transparent text-sm outline-none placeholder:text-slate-500" />
        </div>
        <button className="grid h-12 w-12 place-items-center rounded-2xl bg-gold text-ink" data-testid="home-filter-btn"><SlidersHorizontal size={18} /></button>
      </div>

      {/* Nearby missions banner */}
      {nearby.length > 0 && (
        <button
          onClick={() => navigate("/campaigns")}
          data-testid="nearby-missions-banner"
          className="flex w-full items-center gap-3 rounded-2xl border border-emerald-400/40 bg-emerald-400/10 p-4 text-left"
        >
          <div className="grid h-11 w-11 place-items-center rounded-full bg-emerald-400/20 text-emerald-300"><Target size={22} /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-emerald-300">{nearby.length} {t("missionsNear")} !</div>
            <div className="text-xs text-slate-300">{t("viewMissions")}</div>
          </div>
          <ChevronRight className="text-emerald-300" />
        </button>
      )}

      {/* Real map */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">{t("nearYou")}</h2>
          <span className="text-xs text-slate-400">{t("withinRadius")}</span>
        </div>
        <div className="mb-2 space-y-2" data-testid="map-filters">
          <div className="no-scrollbar flex gap-1.5 overflow-x-auto">
            <button onClick={() => setFCat("all")} className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${fCat === "all" ? "bg-gold text-ink" : "bg-card2/60 text-slate-300"}`}>{t("allCat")}</button>
            {cats.map((c) => (
              <button key={c} onClick={() => setFCat(c)} data-testid={`filter-cat-${c}`} className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${fCat === c ? "bg-gold text-ink" : "bg-card2/60 text-slate-300"}`}>{c}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={fDist} onChange={(e) => setFDist(Number(e.target.value))} data-testid="filter-distance"
              className="rounded-xl border border-cardline bg-ink2/60 px-3 py-2 text-xs text-slate-200 outline-none focus:border-gold">
              <option value={0} className="bg-ink2">{t("distance")}: {t("allCat")}</option>
              <option value={5} className="bg-ink2">≤ 5 km</option>
              <option value={10} className="bg-ink2">≤ 10 km</option>
              <option value={25} className="bg-ink2">≤ 25 km</option>
            </select>
            <select value={fBudget} onChange={(e) => setFBudget(Number(e.target.value))} data-testid="filter-budget"
              className="rounded-xl border border-cardline bg-ink2/60 px-3 py-2 text-xs text-slate-200 outline-none focus:border-gold">
              <option value={0} className="bg-ink2">{t("minBudget")}: {t("allCat")}</option>
              <option value={200} className="bg-ink2">≥ 200 €</option>
              <option value={400} className="bg-ink2">≥ 400 €</option>
            </select>
          </div>
        </div>
        <LiveMap businesses={fBusinesses} missions={fMissions} userPos={pos} onLocate={() => locate(true)} height="18rem" />
      </div>

      {/* Suggestions */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">{t("suggestedForYou")}</h2>
          <button className="text-xs font-semibold text-gold">{t("seeAll")}</button>
        </div>
        <div className="no-scrollbar flex gap-3 overflow-x-auto pb-1">
          {businesses.map((b) => <BusinessCard key={b.id} b={b} />)}
        </div>
      </div>

      {/* Categories */}
      <div>
        <h2 className="mb-2 font-heading text-lg font-semibold">{t("popularCategories")}</h2>
        <div className="grid grid-cols-5 gap-2">
          {CATS.map(({ key, icon: Icon }) => (
            <div key={key} className="flex flex-col items-center gap-1.5">
              <div className="grid h-14 w-14 place-items-center rounded-2xl border border-cardline bg-card2/60 text-gold"><Icon size={20} /></div>
              <span className="text-[11px] text-slate-300">{key}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Collab banner */}
      <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-gold to-goldbright p-4 text-ink gold-glow">
        <div className="grid h-11 w-11 place-items-center rounded-full bg-ink/10"><Gift size={22} /></div>
        <div className="flex-1">
          <div className="font-heading font-bold">{t("collabBanner")}</div>
          <div className="text-sm opacity-80">{t("collabBannerSub")}</div>
        </div>
        <ChevronRight />
      </div>
    </div>
  );
}

function BusinessHome() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pos, locate } = useGeo();
  const [creators, setCreators] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get("/creators").then(({ data }) => setCreators(data.creators)).catch(() => {});
    api.get("/campaigns", { params: { mine: true } }).then(({ data }) => setCampaigns(data.campaigns)).catch(() => {});
  }, []);

  const stats = [
    { icon: Users, label: t("kpiCreators"), value: creators.length ? "150+" : "…" },
    { icon: Sparkles, label: t("campaigns"), value: campaigns.length },
    { icon: TrendingUp, label: t("kpiCollabs"), value: campaigns.reduce((a, c) => a + (c.proposals?.length || 0), 0) },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">{t("hello")} {user?.name} 👋</h1>
        <p className="mt-1 text-sm text-slate-400">{t("discoverCreatorsSub")}</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {stats.map((s, i) => (
          <div key={i} className="rounded-2xl border border-cardline bg-card2/60 p-3 text-center">
            <s.icon className="mx-auto text-gold" size={20} />
            <div className="mt-1 font-heading text-xl font-extrabold">{s.value}</div>
            <div className="text-[11px] text-slate-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Map: creators + own missions */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">{t("nearYou")}</h2>
          <span className="text-xs text-slate-400">{t("legendCreator")} · {t("legendMission")}</span>
        </div>
        <LiveMap creators={creators} missions={campaigns} userPos={pos} onLocate={() => locate(true)} height="18rem" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/discover")} data-testid="biz-discover-btn" className="flex items-center justify-between rounded-2xl bg-gold px-4 py-4 font-bold text-ink">
          {t("discoverCreators")} <Users size={18} />
        </button>
        <button onClick={() => navigate("/campaigns")} data-testid="biz-campaign-btn" className="flex items-center justify-between rounded-2xl border border-cardline bg-card2/60 px-4 py-4 font-semibold text-white">
          {t("createCampaign")} <Plus size={18} className="text-gold" />
        </button>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">{t("suggestedForYou")}</h2>
          <button onClick={() => navigate("/discover")} className="text-xs font-semibold text-gold">{t("seeAll")}</button>
        </div>
        <div className="space-y-2">
          {creators.slice(0, 5).map((c) => (
            <button key={c.id} onClick={() => navigate(`/creators/${c.id}`)} data-testid={`home-creator-${c.id}`}
              className="flex w-full items-center gap-3 rounded-2xl border border-cardline bg-card2/60 p-3 text-left">
              <img src={c.avatar} alt={c.name} className="h-12 w-12 rounded-full object-cover" />
              <div className="flex-1">
                <div className="font-heading text-sm font-semibold">{c.name}</div>
                <div className="text-xs text-slate-400">{c.categories?.slice(0, 2).join(" · ")}</div>
              </div>
              <ChevronRight className="text-slate-500" size={18} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  if (user?.role === "commerce") return <BusinessHome />;
  return <CreatorHome />;
}
